import { forwardRef, useMemo } from "react";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";

import type { CSSProperties, ReactNode } from "react";

const statisticStyles = tv({
  slots: {
    base: "flex flex-col",
    title: "text-sm text-gray-500 dark:text-gray-400",
    content: "flex items-baseline gap-1",
    prefix: "text-2xl font-semibold text-gray-900 dark:text-gray-100",
    value: "text-2xl font-semibold text-gray-900 dark:text-gray-100",
    suffix: "text-sm text-gray-500 dark:text-gray-400",
  },
});

export interface StatisticProps {
  /** Title/label displayed above the value */
  title?: ReactNode;
  /** The main value to display */
  value?: ReactNode;
  /** Content to display before the value */
  prefix?: ReactNode;
  /** Content to display after the value */
  suffix?: ReactNode;
  /** Decimal precision for numeric values */
  precision?: number;
  /** Thousands separator for numeric values */
  groupSeparator?: string;
  /** Additional class name for the root element */
  className?: string;
  /** Inline styles for the root element */
  style?: CSSProperties;
  /** Inline styles for the value element */
  valueStyle?: CSSProperties;
  /** Whether the value represents a loading state */
  loading?: boolean;
}

/**
 * Statistic component for displaying statistical values with titles.
 * Compatible with antd Statistic API.
 */
export const Statistic = forwardRef<HTMLDivElement, StatisticProps>(
  (
    {
      title,
      value,
      prefix,
      suffix,
      precision,
      groupSeparator = ",",
      className,
      style,
      valueStyle,
      loading,
    },
    ref
  ) => {
    const styles = statisticStyles();

    const formattedValue = useMemo(() => {
      if (loading) {
        return "-";
      }

      if (value === null || value === undefined) {
        return "-";
      }

      // If value is already a string or ReactNode, return as-is
      if (typeof value !== "number") {
        return value;
      }

      // Format number with precision and group separator
      let formatted = precision !== undefined ? value.toFixed(precision) : String(value);

      // Add group separator for thousands
      if (groupSeparator) {
        const parts = formatted.split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
        formatted = parts.join(".");
      }

      return formatted;
    }, [value, precision, groupSeparator, loading]);

    return (
      <div ref={ref} className={cn(styles.base(), className)} style={style}>
        {title && <div className={styles.title()}>{title}</div>}
        <div className={styles.content()}>
          {prefix && <span className={styles.prefix()}>{prefix}</span>}
          <span className={styles.value()} style={valueStyle}>
            {formattedValue}
          </span>
          {suffix && <span className={styles.suffix()}>{suffix}</span>}
        </div>
      </div>
    );
  }
);

Statistic.displayName = "Statistic";
